import React from 'react';
import { Layout, Text } from '../../../components/core';
import { PageContent, PageFooter, PageHeader, PageLoading } from '../../../components/page';
import { getRoute, useNavigate } from '../../../routes';
import { useTitle } from '../../../utils/useTitle';
import { useGetSurveyStatus } from '../core/hooks/useGetSurveyStatus';
import { CompletedNoActiveSurvey } from './components/CompletedNoActiveSurvey';
import { CompletedSurvey } from './components/CompletedSurvey';
import { IncompleteSurvey } from './components/IncompleteSurvey';
import { NoActiveSurvey } from './components/NoActiveSurvey';
import { NotEligibleSurvey } from './components/NotEligibleSurvey';
import { SurveyErrorFallback } from './components/SurveyErrorFallback';

export const SurveyHomePage = (): JSX.Element => {
  useTitle('Survey Home');
  const navigate = useNavigate();
  const result = useGetSurveyStatus();

  if (result.loading) {
    return <PageLoading pageName="Survey Home" />;
  }

  const goHome = (): void => {
    navigate(getRoute('home', {}));
  };

  const viewResults = (): void => {
    navigate(getRoute('surveyResults', {}));
  };

  const renderContent = (): JSX.Element => {
    if (!result.success) {
      return (
        <Layout.VStack space={4}>
          <Text.para>Something went wrong while fetching the survey data.</Text.para>
        </Layout.VStack>
      );
    }

    const { survey } = result;

    if (survey.status === 'None') {
      return <NoActiveSurvey />;
    }

    if (survey.status === 'NotEligible') {
      return <NotEligibleSurvey onExploreHub={goHome} />;
    }

    if (survey.status === 'Incomplete') {
      return (
        <IncompleteSurvey
          name={survey.name}
          estimatedCompletionMinutes={survey.estimatedCompletionMinutes}
        />
      );
    }

    if (survey.status === 'Completed') {
      return survey.active ? (
        <CompletedSurvey name={survey.name} onViewResults={viewResults} />
      ) : (
        <CompletedNoActiveSurvey name={survey.name} onViewResults={viewResults} />
      );
    }

    // Fallback - should never reach here
    return <SurveyErrorFallback />;
  };

  return (
    <PageContent testID="survey-home-page">
      <PageHeader title="Survey Home" />
      {renderContent()}
      <PageFooter />
    </PageContent>
  );
};
